// **Payment panes** — merchant-configured payment methods that render as
// DeloPay-drawn tiles in the embedded checkout instead of inside a connector's
// own embedded form.
//
// Why they exist: Stripe only offers Apple Pay / Google Pay / Link when the
// *top-level* document's domain is registered as a payment method domain on the
// merchant's Stripe account. In an embedded iframe that domain is the
// merchant's shop, not the DeloPay checkout — so the wallet silently
// disappears. A pane replaces it with our own tile that opens the DeloPay
// hosted checkout at the top level, in a focused single-method view.
//
// Nothing here is Apple-Pay-specific, and since the router published a
// per-connector capability catalogue nothing here is Stripe-specific either:
// the feature is "which of a connector's payment methods render as a pane, and
// how they look".
//
// This module serves SDK consumers: merchants configuring
// `metadata.native_panes` on a merchant connector account programmatically
// (codec), reading the resolved `native_panes` off a checkout payload
// ({@link PaneView}), or building the focused single-method link that goes
// behind their own button ({@link focusedCheckoutUrl}).
//
// The wire shapes are owned by the router (delopay-backend
// `crates/router/src/core/payment_link/native_panes.rs`) — `native_panes` in
// the metadata key, the field names and the enum values are contracts with it
// and with links already in buyers' hands, so the identifiers here were renamed
// off "native pane" and the wire was not.
//
// Capability (which connector may publish which method, on which rail) comes
// from the router at runtime through `connectors.getPanesCatalog()`, not from a
// table here.

// Type-only, so this module still emits no imports and can be executed
// standalone. `types.ts` owns the catalogue's wire shapes because they are
// generated-spec-paired (`scripts/validate-types.ts`); it imports nothing from
// here, so the dependency runs one way.
import type {
  PaneCapability,
  PaneRail,
  PanesCatalogResponse,
  PanesConnectorCatalog,
  PaneSurcharge,
  PaneView,
  PaneVisibility,
  PaneOpenTarget,
  SurchargeDirection,
  PaneNativeElement,
} from './types';

export type {
  PaneCapability,
  PaneRail,
  PanesCatalogResponse,
  PanesConnectorCatalog,
  PaneSurcharge,
  PaneView,
  PaneVisibility,
  PaneOpenTarget,
  SurchargeDirection,
};

// --- Wire types ---------------------------------------------------------

/**
 * The values {@link decodePanes} recognises. Anything else — a value written by
 * a dashboard newer than this SDK — degrades to `always` rather than throwing
 * or dropping the row: an older SDK reading a newer blob is a normal state
 * during a rollout, and a hard failure there takes out a merchant's whole
 * connector config rather than one tile.
 */
const PANE_VISIBILITIES: readonly PaneVisibility[] = ['always', 'embedded_only', 'external_only'];

/**
 * `same_page` and `popup` are kept as written; anything else — including the
 * absent key — is the default `tab`, as it always was.
 */
function parseOpenTarget(raw: unknown): PaneOpenTarget {
  return raw === 'popup' || raw === 'same_page' ? raw : 'tab';
}

function parseVisibility(raw: unknown): PaneVisibility {
  return PANE_VISIBILITIES.find((value) => value === raw) ?? 'always';
}

const PANE_NATIVE_ELEMENTS: readonly PaneNativeElement[] = [
  'everywhere',
  'embedded_only',
  'external_only',
];

/**
 * Read a stored row's native-element choice, tolerantly.
 *
 * Absent decodes to `everywhere`, because that is what the router decodes a
 * missing key to (`admin::NativePaneNativeElement` defaults to it): a row
 * written before the field existed means "the wallet's own button wherever
 * this pane is offered", which is what the tile was always a stand-in for. An
 * unknown word degrades the same way rather than dropping the row — the
 * router's own decode is lenient for the same rolling-deploy reason.
 */
function parseNativeElement(raw: unknown): PaneNativeElement {
  return PANE_NATIVE_ELEMENTS.find((value) => value === raw) ?? 'everywhere';
}

/**
 * One pane exactly as the merchant configures it. Persisted (JSON) under
 * `metadata.native_panes` on the merchant connector account.
 *
 * Field names are the wire contract — renaming one is a migration. The router
 * decodes strictly row by row: a row that fails strict decoding (e.g. a
 * wrong-typed field like `display_order: "3"`) is dropped whole with a server
 * log, and the remaining rows still render. This SDK's {@link decodePanes} is
 * additionally per-property tolerant — including clamping `display_order` into
 * the router's `i32` range — so a decode→encode round-trip through the SDK
 * repairs a blob the router would partially drop.
 */
export interface Pane {
  /** Catalogue key of the promoted method — see {@link paneMethodInfo}. */
  method: string;
  /** Disabled rows keep their tuning but never reach a buyer. */
  enabled: boolean;
  /** Default-language tile label. Empty falls back to the catalogue name. */
  label: string;
  /** Per-locale overrides of `label`, keyed by checkout locale (`de`, `de-AT`). */
  labelTranslations: Record<string, string>;
  /**
   * Secondary line under the label. `null` means "use the catalogue default";
   * an empty string means the merchant deliberately hid the line. That
   * distinction is the whole reason this is nullable and `label` is not.
   */
  sublabel: string | null;
  /** Per-locale overrides of `sublabel`. */
  sublabelTranslations: Record<string, string>;
  /** Section the tile groups under. Empty falls back to the catalogue category. */
  category: string;
  /** Built-in icon key — see {@link PANE_ICON_KEYS}. */
  icon: string;
  /** Custom inline SVG. Sanitized server-side before it reaches a buyer; a
   *  rejected payload falls back to the built-in `icon`. */
  iconSvg: string;
  /** Lower renders first; ties break on catalogue order. */
  displayOrder: number;
  /** Where the tile is offered — see {@link PaneVisibility}. */
  visibility: PaneVisibility;
  /** How the embedded checkout opens the focused view — see {@link PaneOpenTarget}. */
  openIn: PaneOpenTarget;
  /**
   * Where the wallet's own button replaces this tile — see
   * {@link PaneNativeElement}. Only meaningful on a method whose catalogue
   * entry says {@link PaneCapability.supports_native_element}; the router
   * ignores it elsewhere. `everywhere` is the default and what a row stored
   * before the field existed decodes to.
   */
  nativeElement: PaneNativeElement;
  /** A shipped look instead of the icon (`paypal`, `card`); `''` or absent = none.
   *  Optional because a `Pane` built before the field existed must still encode. */
  preset?: string;
  /** The preset's style; `''` or absent = the preset's default. */
  style?: string;
}

/**
 * Read a resolved tile's placement rule, across every payload version.
 *
 * Prefer this to touching either field. They are independent on the wire — a
 * payload may carry either, both or neither — and each alone loses a placement
 * rule: `embedded_only` cannot express `external_only`, so an `external_only`
 * tile read through it renders inside the merchant iframe the merchant asked
 * to keep it out of; `visibility` is absent on a router that predates it, so
 * an `embedded_only` tile read through it alone renders at top level too,
 * duplicating a method Stripe's own form already offers there.
 *
 * A recognised `visibility` wins. Otherwise the legacy boolean answers: absent
 * (old router) it carries the only placement that router could express, and
 * unrecognised (newer router) the router still sets it under its own fixed
 * rule. Neither path throws, and both degrade toward showing a tile — losing a
 * placement rule, never stranding a method.
 */
export function paneViewVisibility(
  view: Pick<PaneView, 'visibility' | 'embedded_only'>,
): PaneVisibility {
  const declared = PANE_VISIBILITIES.find((value) => value === view.visibility);
  if (declared) return declared;
  return view.embedded_only === true ? 'embedded_only' : 'always';
}

// --- Catalogue ----------------------------------------------------------

/**
 * A method the frozen Stripe fallback table knows, with the compiled copy the
 * router shipped for it at the time.
 *
 * Only {@link STRIPE_FALLBACK_PANE_METHODS} is typed with this. The live
 * catalogue (`connectors.getPanesCatalog()`) carries capability facts and no
 * copy at all, by design — see {@link PaneCapability}.
 */
export interface PaneMethodInfo {
  key: string;
  rail: PaneRail;
  /** Confirm routing key. Pass through verbatim; never derive it. */
  paymentMethod: string;
  /** Confirm routing key. Pass through verbatim; never derive it. */
  paymentMethodType: string;
  /** The focused checkout has to collect a billing country to confirm this. */
  requiresBillingCountry: boolean;
  /** Catalogue default label, shown as the editor's placeholder. */
  defaultLabel: string;
  /** Catalogue default sub-text. */
  defaultSublabel: string;
  /** Catalogue default section. */
  defaultCategory: string;
  /** Catalogue default icon key. */
  defaultIcon: string;
}

/**
 * What this SDK assumes when the router cannot answer.
 *
 * The capability endpoint does not exist on an older router, which answers
 * `connectors.getPanesCatalog()` with a 404.
 * Rather than leaving a caller with nothing, the pane helpers default to
 * exactly the behaviour they had before that endpoint existed: panes on the
 * Stripe connector only, with the method set the router's Stripe catalogue had
 * at the time.
 *
 * **This is a degradation path, not a mirror. Do not keep it in sync by hand,
 * and do not add a connector or a method to it** — that is what this table was
 * before this ticket, and adding to it would quietly rebuild the mirror. A
 * connector the router serves panes for arrives through the endpoint and needs
 * no entry here; a connector that needs an entry here to appear is a connector
 * the router will not deliver panes for anyway.
 *
 * The only reason to touch this list is to correct what Stripe's catalogue
 * looked like *before* the endpoint existed, and that is history, so there is
 * no such reason. Once the endpoint is reachable it is never consulted for a
 * connector the router answered for.
 *
 * The redirect-rail set is deliberately narrow — every entry is a variant the
 * Stripe connector accepts and that needs no extra buyer input beyond a
 * billing country. `eps` / `p24` / `bancontact` are absent because Stripe
 * hard-requires billing fields the focused checkout never collects (a full
 * name for EPS and Bancontact, an email for Przelewy24), so their tiles could
 * never succeed.
 *
 * `defaultLabel` / `defaultSublabel` are the copy the router compiled at that
 * time and are frozen with the rest of the table. Copy for a method the live
 * catalogue reports belongs to the consumer's own locale files, keyed by
 * `key` — the router ships none.
 */
export const STRIPE_FALLBACK_PANE_METHODS: readonly PaneMethodInfo[] = [
  {
    key: 'apple_pay',
    rail: 'wallet',
    paymentMethod: 'wallet',
    paymentMethodType: 'apple_pay',
    requiresBillingCountry: false,
    defaultLabel: 'Apple Pay',
    defaultSublabel: 'Pay with Apple Pay',
    defaultCategory: 'wallet',
    defaultIcon: 'apple',
  },
  {
    key: 'google_pay',
    rail: 'wallet',
    paymentMethod: 'wallet',
    paymentMethodType: 'google_pay',
    requiresBillingCountry: false,
    defaultLabel: 'Google Pay',
    defaultSublabel: 'Pay with Google Pay',
    defaultCategory: 'wallet',
    defaultIcon: 'google',
  },
  {
    key: 'link',
    rail: 'wallet',
    paymentMethod: 'wallet',
    paymentMethodType: 'link',
    requiresBillingCountry: false,
    defaultLabel: 'Link',
    defaultSublabel: 'Pay with saved details',
    defaultCategory: 'wallet',
    defaultIcon: 'wallet',
  },
  {
    key: 'klarna',
    rail: 'redirect',
    paymentMethod: 'pay_later',
    paymentMethodType: 'klarna',
    // Stripe's Klarna needs the buyer's country echoed into the confirm body.
    requiresBillingCountry: true,
    defaultLabel: 'Klarna',
    // NOT the router's current "Pay later or in 3 instalments". This table is
    // what a router predating the capability endpoint renders, and that copy
    // only gained the "3" in the same release that added the endpoint.
    // Restating the new string here
    // would put copy no reachable router emits in front of a merchant, and
    // would change what the deprecated `nativePaneMethodInfo` has returned
    // since 0.106.0.
    defaultSublabel: 'Pay later or in instalments',
    defaultCategory: 'bnpl',
    defaultIcon: 'bnpl',
  },
  {
    key: 'affirm',
    rail: 'redirect',
    paymentMethod: 'pay_later',
    paymentMethodType: 'affirm',
    requiresBillingCountry: false,
    defaultLabel: 'Affirm',
    defaultSublabel: 'Pay over time',
    defaultCategory: 'bnpl',
    defaultIcon: 'bnpl',
  },
  {
    key: 'ideal',
    rail: 'redirect',
    paymentMethod: 'bank_redirect',
    paymentMethodType: 'ideal',
    requiresBillingCountry: false,
    defaultLabel: 'iDEAL',
    defaultSublabel: 'Pay from your bank',
    defaultCategory: 'bank_redirect',
    defaultIcon: 'bank',
  },
  {
    key: 'alipay',
    rail: 'redirect',
    paymentMethod: 'wallet',
    paymentMethodType: 'ali_pay',
    requiresBillingCountry: false,
    defaultLabel: 'Alipay',
    defaultSublabel: 'Pay with Alipay',
    defaultCategory: 'wallet',
    defaultIcon: 'wallet',
  },
  {
    key: 'revolut_pay',
    rail: 'redirect',
    paymentMethod: 'wallet',
    paymentMethodType: 'revolut_pay',
    requiresBillingCountry: false,
    defaultLabel: 'Revolut Pay',
    defaultSublabel: 'Pay with Revolut',
    defaultCategory: 'wallet',
    defaultIcon: 'wallet',
  },
  {
    key: 'amazon_pay',
    rail: 'redirect',
    paymentMethod: 'wallet',
    paymentMethodType: 'amazon_pay',
    requiresBillingCountry: false,
    defaultLabel: 'Amazon Pay',
    defaultSublabel: 'Pay with Amazon',
    defaultCategory: 'wallet',
    defaultIcon: 'wallet',
  },
];

/**
 * {@link STRIPE_FALLBACK_PANE_METHODS} in the shape the live endpoint returns,
 * so every helper below takes one type of catalogue and the degradation path
 * is not a second code path. Derived, not written out: two spellings of one
 * table is exactly the drift this ticket removed.
 */
export const STRIPE_FALLBACK_PANE_CATALOG: PanesConnectorCatalog = {
  connector: 'stripe',
  allowed_rails: ['wallet', 'redirect'],
  methods: STRIPE_FALLBACK_PANE_METHODS.map((info) => ({
    key: info.key,
    rail: info.rail,
    payment_method: info.paymentMethod,
    payment_method_type: info.paymentMethodType,
    requires_billing_country: info.requiresBillingCountry,
  })),
};

/**
 * An empty catalogue — a connector the router does not offer panes for.
 * Exported so callers can express "asked, and the answer was no" without
 * reaching for `null` in the middle of a computation.
 */
export function emptyPaneCatalog(connector: string): PanesConnectorCatalog {
  return { connector, allowed_rails: [], methods: [] };
}

/**
 * Pull one connector's entry out of a `connectors.getPanesCatalog()` response.
 *
 * Returns {@link emptyPaneCatalog} for a connector the response does not name:
 * "the router knows this connector and publishes no panes for it" and "the
 * router has never heard of it" are the same answer to a caller building an
 * editor, and both mean "offer nothing".
 */
export function paneCatalogFor(
  response: PanesCatalogResponse,
  connector: string,
): PanesConnectorCatalog {
  return (
    response.connectors.find((entry) => entry.connector === connector) ??
    emptyPaneCatalog(connector)
  );
}

/**
 * Built-in tile icon keys the buyer-facing checkout ships a glyph for.
 *
 * Reconciled against both ends: every key here has a branch in the checkout's
 * `NativePaneIcon.svelte`, and every `default_icon` the router's catalogue
 * publishes is in this list. An icon key the checkout does not ship falls
 * through to `wallet` there, so a key that drifts in renders as the wrong
 * glyph rather than as nothing.
 */
export const PANE_ICON_KEYS: readonly string[] = [
  'wallet',
  'card',
  'bank',
  'apple',
  'google',
  'bnpl',
  'cash',
  // Brand marks, not category glyphs: the checkout draws PayPal's monogram,
  // Klarna's squircle-K and the Bitcoin mark for these, the same logos the
  // bespoke panes they replace already drew. Listed here so a merchant's own
  // dashboard can offer them; the router decides which one a tile *defaults*
  // to, and a router predating them simply keeps sending the old default.
  'paypal',
  'klarna',
  'crypto',
];

/**
 * Section keys a pane's tile may group under.
 *
 * Reconciled against the router's actual `default_category` values rather than
 * against what the Stripe table happened to need. `crypto` and `game_items`
 * are router defaults (the Cryptomus/NowPayments and Skinsback catalogues) and
 * were missing here, so a merchant configuring one of those panes was shown a
 * category their editor did not recognise.
 *
 * A custom value is allowed and renders as its own section under that literal
 * text. The buyer-facing checkout translates a subset of these into a section
 * header and falls through to the raw key for the rest — `crypto` and
 * `game_items` are the two it has yet to add.
 */
export const PANE_CATEGORY_KEYS: readonly string[] = [
  'wallet',
  'card',
  'bnpl',
  'bank_redirect',
  'bank_transfer',
  'crypto',
  'game_items',
  'cash',
];

/**
 * Mirror of the router's per-connector cap. Counted differently on each side:
 * {@link decodePanes} stops after 12 *decoded* rows (entries with a usable,
 * non-duplicate `method` — junk and duplicate entries don't consume a slot),
 * while the router caps *accepted* panes (enabled, known, deduplicated) at 12
 * — so an oversized hand-written blob may render a pane this decoder drops.
 * Blobs the SDK itself encodes never exceed the cap.
 */
export const PANES_MAX = 12;

/**
 * Look a method up in one connector's catalogue.
 *
 * Pass the entry for the connector being configured, from
 * `connectors.getPanesCatalog()` via {@link paneCatalogFor}. The default is
 * the degradation path and only correct for Stripe on a router that predates
 * the endpoint — see {@link STRIPE_FALLBACK_PANE_CATALOG}.
 */
export function paneMethodInfo(
  method: string,
  catalog: PanesConnectorCatalog = STRIPE_FALLBACK_PANE_CATALOG,
): PaneCapability | undefined {
  return catalog.methods.find((info) => info.key === method);
}

/** Whether this connector may declare a pane on `rail` at all. A wallet pane
 *  on a connector outside its `allowed_rails` is refused at save time — the
 *  wallet rail rides on Stripe's Express Checkout Element charging the *same*
 *  payment intent the embedded checkout holds, which does not generalise. */
export function paneRailAllowed(rail: PaneRail, catalog: PanesConnectorCatalog): boolean {
  return catalog.allowed_rails.includes(rail);
}

/**
 * The methods a caller may actually offer for this connector: the catalogue's
 * own list, minus anything on a rail the connector does not allow.
 *
 * The router should not report such a pair in the first place, but the two
 * fields are independent in the response and a save that crosses them is
 * refused at the API with an error naming the connector and the method. Filter
 * rather than trust, so a merchant never configures a tile the save will
 * bounce.
 */
export function offerablePaneMethods(catalog: PanesConnectorCatalog): PaneCapability[] {
  return catalog.methods.filter((info) => paneRailAllowed(info.rail, catalog));
}

/** Non-copy display defaults for a tile: which built-in glyph and which
 *  section heading a new pane starts on. */
export interface PaneDisplayDefaults {
  icon: string;
  category: string;
}

/**
 * Per-method overrides, for the handful whose brand glyph is not what their
 * payment method would suggest. Everything else is derived from the catalogue
 * entry's `payment_method` below, so a connector the SDK has never heard of
 * still gets a sensible tile rather than a blank one.
 *
 * These have to be keyed by method and not by `payment_method`: `wallet` also
 * routes Link, Alipay, Revolut Pay and Amazon Pay, and `pay_later` also routes
 * Affirm — none of which the SDK has a mark for. Getting this wrong is not a
 * cosmetic slip that the next release fixes, because {@link defaultPane}
 * materialises the value and {@link encodePanes} writes it to
 * `metadata.native_panes`, where a stored `icon` outranks the router's
 * `default_icon` for the life of the pane.
 */
const DISPLAY_DEFAULTS_BY_KEY: Readonly<Record<string, PaneDisplayDefaults>> = {
  apple_pay: { icon: 'apple', category: 'wallet' },
  google_pay: { icon: 'google', category: 'wallet' },
  paypal: { icon: 'paypal', category: 'wallet' },
  // Both the Klarna connector's own tile and Stripe's Klarna. Affirm stays on
  // the generic `bnpl` glyph — same rail, no mark of its own.
  klarna: { icon: 'klarna', category: 'bnpl' },
};

/**
 * Derived from the `payment_method` half of the catalogue's routing pair.
 * Every value here reproduces the router's own `default_icon` /
 * `default_category` for the methods it publishes today, so a tile the SDK
 * lays out and a tile the router lays out agree.
 */
const DISPLAY_DEFAULTS_BY_PAYMENT_METHOD: Readonly<Record<string, PaneDisplayDefaults>> = {
  wallet: { icon: 'wallet', category: 'wallet' },
  card: { icon: 'card', category: 'card' },
  card_redirect: { icon: 'card', category: 'card' },
  pay_later: { icon: 'bnpl', category: 'bnpl' },
  bank_redirect: { icon: 'bank', category: 'bank_redirect' },
  bank_transfer: { icon: 'bank', category: 'bank_transfer' },
  bank_debit: { icon: 'bank', category: 'bank_transfer' },
  // Cryptomus and NowPayments publish the same `crypto` key on this rail, and
  // the router defaults both to the Bitcoin mark.
  crypto: { icon: 'crypto', category: 'crypto' },
  // Skinsback, which has no mark: the router defaults it to `wallet` too.
  game_items: { icon: 'wallet', category: 'game_items' },
  cash: { icon: 'cash', category: 'cash' },
  voucher: { icon: 'cash', category: 'cash' },
};

const DISPLAY_DEFAULTS_FALLBACK: PaneDisplayDefaults = { icon: 'wallet', category: 'wallet' };

/**
 * What a new tile looks like before the merchant designs it.
 *
 * The router serves capability, not presentation, so the glyph and the section
 * come from here and the accompanying text comes from the caller's own locale
 * files. A method with no entry at all still resolves — that is the point of
 * deriving from `payment_method`: a connector added to the router's catalogue
 * tomorrow needs no SDK release to be designable today.
 */
export function paneDisplayDefaults(info: PaneCapability | undefined): PaneDisplayDefaults {
  if (!info) return DISPLAY_DEFAULTS_FALLBACK;
  return (
    DISPLAY_DEFAULTS_BY_KEY[info.key] ??
    DISPLAY_DEFAULTS_BY_PAYMENT_METHOD[info.payment_method] ??
    DISPLAY_DEFAULTS_FALLBACK
  );
}

// --- Validation ---------------------------------------------------------

/**
 * Why a configured pane would not survive a save or a render.
 *
 * - `method_unknown` — the connector's catalogue does not publish this key, so
 *   the router drops the row at render.
 * - `rail_not_allowed` — the method's rail is not in the connector's
 *   `allowed_rails`; the API refuses this at save time.
 * - `over_cap` — past the router's per-connector cap of {@link PANES_MAX}; the
 *   row is stored but never rendered.
 * - `duplicate_method` — an earlier enabled row already claims this method.
 *   The router skips disabled rows and then dedupes, so the first *enabled*
 *   row renders and every later one is dropped in silence. Reported on the
 *   losing rows, never on the one that survives.
 */
export type PaneIssueCode = 'method_unknown' | 'rail_not_allowed' | 'over_cap' | 'duplicate_method';

export interface PaneIssue {
  /** Index into the list handed to {@link validatePanes}. */
  index: number;
  method: string;
  code: PaneIssueCode;
}

/**
 * Check a configured list against one connector's catalogue, before writing it
 * to `metadata.native_panes`.
 *
 * The API applies the same rules at save time, so this changes where the
 * merchant finds out, not whether they do — and an SDK caller building an
 * editor would otherwise learn it from a bounced request with no row to point
 * at. An empty result means nothing here will be refused or silently dropped.
 */
export function validatePanes(
  panes: readonly Pane[],
  catalog: PanesConnectorCatalog = STRIPE_FALLBACK_PANE_CATALOG,
): PaneIssue[] {
  const issues: PaneIssue[] = [];
  // Keyed on the catalogue's own key rather than the raw string, because that
  // is what the router dedupes on. Only enabled rows are recorded: a disabled
  // row never reaches the router's `seen` set, so it neither claims a method
  // nor loses to one.
  const claimedBy = new Set<string>();
  panes.forEach((pane, index) => {
    const info = paneMethodInfo(pane.method, catalog);
    if (!info) {
      issues.push({ index, method: pane.method, code: 'method_unknown' });
    } else if (!paneRailAllowed(info.rail, catalog)) {
      issues.push({ index, method: pane.method, code: 'rail_not_allowed' });
    } else if (pane.enabled) {
      // Unknown and rail-refused rows are dropped before the router dedupes,
      // so they cannot claim a key — and reporting two codes for one row would
      // point the merchant at the wrong repair.
      if (claimedBy.has(info.key)) {
        issues.push({ index, method: pane.method, code: 'duplicate_method' });
      } else {
        claimedBy.add(info.key);
      }
    }
    if (index >= PANES_MAX) issues.push({ index, method: pane.method, code: 'over_cap' });
  });
  return issues;
}

// --- Codec --------------------------------------------------------------

/**
 * A new pane for `method`, with its glyph and section resolved from the
 * connector's catalogue entry rather than from a Stripe-keyed table — so
 * `defaultPane('creem_checkout', catalog)` is a designed tile and not a blank
 * one.
 *
 * Pass the catalogue entry for the connector being configured; the default is
 * the degradation path (see {@link STRIPE_FALLBACK_PANE_CATALOG}). Labels are
 * deliberately left empty: empty means "use the catalogue default", which the
 * router resolves at render, and the router ships no copy for a caller to
 * mirror.
 */
export function defaultPane(
  method: string,
  catalog: PanesConnectorCatalog = STRIPE_FALLBACK_PANE_CATALOG,
): Pane {
  const defaults = paneDisplayDefaults(paneMethodInfo(method, catalog));
  return {
    method,
    enabled: true,
    label: '',
    labelTranslations: {},
    sublabel: null,
    sublabelTranslations: {},
    category: defaults.category,
    icon: defaults.icon,
    iconSvg: '',
    displayOrder: 0,
    visibility: 'always',
    openIn: 'tab',
    nativeElement: 'everywhere',
    preset: '',
    style: '',
  };
}

export function clonePane(pane: Pane): Pane {
  return {
    ...pane,
    labelTranslations: { ...pane.labelTranslations },
    sublabelTranslations: { ...pane.sublabelTranslations },
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseTranslations(raw: unknown): Record<string, string> {
  if (!isObject(raw)) return {};
  const out: Record<string, string> = {};
  for (const [locale, value] of Object.entries(raw)) {
    if (typeof value === 'string' && value.length > 0) out[locale] = value;
  }
  return out;
}

function str(raw: unknown): string {
  return typeof raw === 'string' ? raw : '';
}

// The router's wire type is an `i32` (`api_models` `display_order`), and a
// fractional or out-of-range value fails its strict row decode — dropping the
// whole row server-side while an SDK round-trip would keep the blob looking
// healthy. Clamped on BOTH decode and encode so this codec never emits a
// value the router rejects, regardless of where the number came from.
const DISPLAY_ORDER_MIN = -2147483648;
const DISPLAY_ORDER_MAX = 2147483647;

function clampDisplayOrder(raw: number): number {
  if (!Number.isFinite(raw)) return 0;
  return Math.min(DISPLAY_ORDER_MAX, Math.max(DISPLAY_ORDER_MIN, Math.trunc(raw)));
}

/**
 * Decode the stored `metadata.native_panes` blob into editor rows.
 *
 * Tolerant like the branding codecs: anything malformed falls back per
 * property, rows without a usable `method` are dropped, duplicates keep the
 * first occurrence — except that an enabled row wins over an earlier disabled
 * one for the same method, because that is the row the router renders — and
 * decoding stops after {@link PANES_MAX} decoded rows (dropped junk/duplicate
 * entries don't consume a slot). That is more forgiving than the router, which
 * drops a strict-decode-failing row whole (keeping the rest) and caps accepted
 * panes rather than decoded rows — see {@link Pane} and {@link PANES_MAX}.
 * Returns `null` when the input is not an array so the caller can distinguish
 * "never configured" from "cleared".
 */
export function decodePanes(raw: unknown): Pane[] | null {
  if (!Array.isArray(raw)) return null;
  const decodeRow = (entry: Record<string, unknown>, method: string): Pane => ({
    method,
    enabled: entry['enabled'] !== false,
    label: str(entry['label']),
    labelTranslations: parseTranslations(entry['label_translations']),
    sublabel: typeof entry['sublabel'] === 'string' ? entry['sublabel'] : null,
    sublabelTranslations: parseTranslations(entry['sublabel_translations']),
    category: str(entry['category']),
    icon: str(entry['icon']),
    iconSvg: str(entry['icon_svg']),
    displayOrder: clampDisplayOrder(Number(entry['display_order'])),
    // Anything unrecognised degrades to `always` rather than dropping the row.
    visibility: parseVisibility(entry['visibility']),
    // Same tolerance: an unknown value degrades to the default `tab`.
    openIn: parseOpenTarget(entry['open_in']),
    nativeElement: parseNativeElement(entry['native_element']),
    preset: str(entry['preset']),
    style: str(entry['style']),
  });

  const indexByMethod = new Map<string, number>();
  const out: Pane[] = [];
  for (const entry of raw) {
    if (out.length >= PANES_MAX) break;
    if (!isObject(entry)) continue;
    const method = str(entry['method']).trim();
    if (!method) continue;
    const kept = indexByMethod.get(method);
    if (kept !== undefined) {
      // Duplicate method. Keep the row the ROUTER would render: it skips
      // disabled rows BEFORE deduping (`native_panes.rs`), so where a blob
      // holds both a disabled and an enabled row for one method, buyers see
      // the enabled one. Keeping the disabled row here would show the merchant
      // a pane that is off while it is live, and persist that on the next save.
      const enabled = entry['enabled'] !== false;
      const existing = out[kept];
      if (enabled && existing && !existing.enabled) out[kept] = decodeRow(entry, method);
      continue;
    }
    indexByMethod.set(method, out.length);
    out.push(decodeRow(entry, method));
  }
  return out;
}

/**
 * Encode editor rows back into the snake_case blob the connector account
 * stores. Empty optional strings are omitted so the metadata stays small and a
 * merchant who typed nothing round-trips as "use the catalogue default" rather
 * than as an explicit empty override.
 *
 * `sublabel` is the exception: an explicitly-empty value is preserved (as `""`)
 * because that is how a merchant hides the second line.
 */
export function encodePanes(panes: Pane[]): Record<string, unknown>[] {
  const nonEmpty = (map: Record<string, string>): Record<string, string> | undefined => {
    const entries = Object.entries(map).filter(([, v]) => v.trim().length > 0);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  };
  return panes.slice(0, PANES_MAX).map((pane) => {
    const labelTranslations = nonEmpty(pane.labelTranslations);
    const sublabelTranslations = nonEmpty(pane.sublabelTranslations);
    return {
      method: pane.method,
      enabled: pane.enabled,
      ...(pane.label.trim() ? { label: pane.label.trim() } : {}),
      ...(labelTranslations ? { label_translations: labelTranslations } : {}),
      // `null` omits the key entirely (catalogue default wins); `''` is sent
      // as-is because that is how the merchant hides the second line.
      ...(pane.sublabel !== null ? { sublabel: pane.sublabel } : {}),
      ...(sublabelTranslations ? { sublabel_translations: sublabelTranslations } : {}),
      ...(pane.category.trim() ? { category: pane.category.trim() } : {}),
      ...(pane.icon.trim() ? { icon: pane.icon.trim() } : {}),
      ...(pane.iconSvg.trim() ? { icon_svg: pane.iconSvg.trim() } : {}),
      // Clamped on encode too, not only decode: the router's strict i32 row
      // decode drops a row whole for a fractional or out-of-range value, so
      // writing e.g. 3.5 or Date.now() verbatim would silently delete the
      // pane at render while every read surface shows it healthy.
      display_order: clampDisplayOrder(pane.displayOrder),
      // Normalised on encode for the same reason `display_order` is clamped:
      // the router's strict row decode drops a row whole on a value outside
      // its enum, and a caller who built a `Pane` by hand (or in plain JS)
      // never went through `decodePanes`. A decoded pane always holds a valid
      // value, so this is a no-op on the round-trip path.
      visibility: parseVisibility(pane.visibility),
      open_in: parseOpenTarget(pane.openIn),
      // Omitted at the default: `everywhere` is what the router decodes an
      // absent key to, so leaving it out changes nothing — and keeps a blob
      // stored before the field existed byte-identical on re-encode, which
      // is what the wire-compatibility test pins.
      ...(parseNativeElement(pane.nativeElement) === 'everywhere'
        ? {}
        : { native_element: parseNativeElement(pane.nativeElement) }),
      // Absent and blank both mean "no look": a pane shaped before the fields
      // existed encodes exactly as it always did.
      ...((pane.preset ?? '').trim() ? { preset: (pane.preset ?? '').trim() } : {}),
      ...((pane.style ?? '').trim() ? { style: (pane.style ?? '').trim() } : {}),
    };
  });
}

// --- Focused checkout URL ----------------------------------------------

export interface FocusedCheckoutUrlParams {
  /** Base URL of the DeloPay hosted checkout, e.g. `https://checkout.delopay.net`. */
  checkoutBaseUrl: string;
  merchantId: string;
  paymentId: string;
  /** Pane method key to focus on (`apple_pay`, `klarna`, …). */
  method: string;
  /**
   * Connector that owns the tile, forwarded as `connector=`.
   *
   * A bare `pane=` resolves to the **first payable match in server-sorted
   * order**, which is unambiguous only while one connector publishes a given
   * key. Stripe publishes `klarna` and so does the Klarna connector, so a deep
   * link that names neither charges through whichever tile happens to sort
   * first. Name the connector whenever you know it.
   *
   * Optional because links minted before this parameter existed are already in
   * merchants' pages and still have to resolve.
   */
  connector?: string;
  /**
   * Merchant connector ACCOUNT that owns the tile, forwarded as `mca=`.
   *
   * Needed for the same reason `connector` is, one level down: a profile may
   * hold two enabled accounts of one connector, each publishing its own tile,
   * and the two links would otherwise be identical. Only meaningful alongside
   * `connector`.
   */
  merchantConnectorId?: string;
  /** Optional buyer locale, forwarded as `?locale=`. */
  locale?: string;
  /**
   * Set when the merchant's checkout-custom-field answers are already
   * persisted on the payment — forwarded as `cf=1` so the focused view skips
   * asking a second time. Purely a UI hint: the values live on the intent
   * either way, and the backend only accepts the merchant's configured field
   * keys from a client, so a wrongly-set flag can at worst skip an
   * informational prompt. This is the same hint the embedded pane sets when
   * it opens the focused view.
   */
  customFieldsCollected?: boolean;
}

/**
 * Build the link to the **focused single-method checkout**: the DeloPay hosted
 * checkout rendered with one payment method, one button, no picker.
 *
 * Two callers:
 *   - the embedded checkout, which opens this in a new tab when a buyer clicks
 *     a pane tile, and
 *   - a merchant running their own checkout, who puts it behind their own
 *     button — the same mechanism without an iframe.
 *
 * `method` is not limited to configured panes. A configured pane gets the
 * focused one-button view; `card`, `paypal`, `crypto_currency` and the
 * local-methods catalogs (by method key or vendor code) open the checkout
 * pinned to that method. Methods that exist only as a tab inside a connector's
 * own embedded form — iDEAL, Bancontact, P24 and the like, unless promoted to
 * a pane — cannot be isolated, because the connector owns that surface. An
 * unknown or unavailable method is never a dead end: the checkout shows a
 * notice with a visible "show all payment methods" action.
 *
 * Pass {@link FocusedCheckoutUrlParams.connector} (and
 * {@link FocusedCheckoutUrlParams.merchantConnectorId}) whenever you know
 * them. Without them the method key alone picks the first payable match, and
 * more than one connector can publish the same key. Omitting them produces
 * exactly the URL this function has always produced, so links already minted
 * are unaffected.
 *
 * Open it **at the top level** (a new tab or a full-page navigation). The whole
 * point is that the top-level domain is the registered payment method domain;
 * rendering it in an iframe puts you back where you started.
 *
 * If you open it with `window.open`, call that **synchronously inside the click
 * handler** or the popup blocker will eat it, and make sure any iframe you
 * render DeloPay in permits popups (`allow-popups`, plus
 * `allow-popups-to-escape-sandbox` under a restrictive `sandbox`).
 */
export function focusedCheckoutUrl(params: FocusedCheckoutUrlParams): string {
  const base = params.checkoutBaseUrl.replace(/\/+$/, '');
  const path = `${base}/pay/${encodeURIComponent(params.merchantId)}/${encodeURIComponent(
    params.paymentId,
  )}`;
  const query = new URLSearchParams({ pane: params.method });
  // Parameter order matches the embedded checkout's own `focusedUrl`
  // (`NativePanesPane.svelte`), so a merchant's deep link and a tile click
  // produce the same string for the same tile. A blank value is treated as
  // absent rather than emitted empty: `connector=` would otherwise read as a
  // connector named "" instead of as "not specified".
  const connector = params.connector?.trim();
  if (connector) query.set('connector', connector);
  const merchantConnectorId = params.merchantConnectorId?.trim();
  if (merchantConnectorId) query.set('mca', merchantConnectorId);
  if (params.locale) query.set('locale', params.locale);
  if (params.customFieldsCollected) query.set('cf', '1');
  return `${path}?${query.toString()}`;
}

// --- Buyer-side checkout events ----------------------------------------

/**
 * The closed vocabulary of buyer-side checkout events recorded on the payment's
 * status timeline. Written through
 * `POST /payment-link/{merchant_id}/{payment_id}/checkout-events`, authorized
 * with the payment's `client_secret` as a bearer token.
 *
 * The `native_pane_` prefix is the router's enum on the wire and does not move
 * with the rename — see the module header.
 */
export const CHECKOUT_EVENT_KINDS = [
  'native_pane_selected',
  'native_pane_tab_opened',
  'native_pane_tab_blocked',
  'native_pane_abandoned',
  'native_pane_returned',
  'checkout_cancelled',
] as const;

export type CheckoutEventKind = (typeof CHECKOUT_EVENT_KINDS)[number];

// --- Deprecated aliases -------------------------------------------------
//
// The pre-0.109.0 names, kept so an external `@delopay/sdk` consumer upgrades
// on their own schedule rather than on ours. **Scheduled for removal in
// 0.112.0** — see CHANGELOG.md. Nothing in this repo uses them.

/** @deprecated Renamed to {@link Pane}. Removed in 0.112.0. */
export type StripeNativePane = Pane;
/** @deprecated Renamed to {@link PaneView}. Removed in 0.112.0. */
export type NativePaneView = PaneView;
/** @deprecated Renamed to {@link PaneVisibility}. Removed in 0.112.0. */
export type NativePaneVisibility = PaneVisibility;
/** @deprecated Renamed to {@link PaneOpenTarget}. Removed in 0.112.0. */
export type NativePaneOpenTarget = PaneOpenTarget;
/** @deprecated Spelt {@link PaneNativeElement} on the SDK side. Removed in 0.112.0. */
export type NativePaneNativeElement = PaneNativeElement;
/**
 * @deprecated Superseded by {@link PaneMethodInfo}. Removed in 0.112.0.
 *
 * Deliberately **not** an alias of `PaneMethodInfo`: that type gained three
 * required routing fields (`paymentMethod`, `paymentMethodType`,
 * `requiresBillingCountry`), and a consumer who wrote an object literal or a
 * function return against the 0.108 shape would stop compiling on a deprecated
 * name that promises the opposite. This stays the six fields that shape had.
 *
 * `PaneMethodInfo` is structurally assignable to it, so everything this SDK
 * hands back — including {@link nativePaneMethodInfo} — still satisfies it.
 */
export interface NativePaneMethodInfo {
  key: string;
  rail: PaneRail;
  /** Catalogue default label, shown as the editor's placeholder. */
  defaultLabel: string;
  /** Catalogue default sub-text. */
  defaultSublabel: string;
  /** Catalogue default section. */
  defaultCategory: string;
  /** Catalogue default icon key. */
  defaultIcon: string;
}

/**
 * @deprecated Renamed to {@link STRIPE_FALLBACK_PANE_METHODS}, which is a
 * degradation path and not a catalogue mirror. Removed in 0.112.0.
 */
export const STRIPE_NATIVE_PANE_METHODS = STRIPE_FALLBACK_PANE_METHODS;
/** @deprecated Renamed to {@link PANE_ICON_KEYS}. Removed in 0.112.0. */
export const NATIVE_PANE_ICON_KEYS = PANE_ICON_KEYS;
/** @deprecated Renamed to {@link PANE_CATEGORY_KEYS}. Removed in 0.112.0. */
export const NATIVE_PANE_CATEGORY_KEYS = PANE_CATEGORY_KEYS;
/** @deprecated Renamed to {@link PANES_MAX}. Removed in 0.112.0. */
export const NATIVE_PANES_MAX = PANES_MAX;

/**
 * @deprecated Superseded by {@link paneMethodInfo}, which takes the connector's
 * catalogue and answers for every connector rather than only for Stripe.
 * Removed in 0.112.0.
 *
 * Kept as its own function rather than as an alias because it must keep its
 * old return type: it answers out of the frozen Stripe table and carries that
 * table's compiled copy, which the live catalogue deliberately does not have.
 */
export function nativePaneMethodInfo(method: string): PaneMethodInfo | undefined {
  return STRIPE_FALLBACK_PANE_METHODS.find((info) => info.key === method);
}

/**
 * @deprecated Superseded by {@link defaultPane}. Removed in 0.112.0.
 *
 * Kept as its own function rather than as an alias because it must keep its
 * old behaviour: it leaves `category` empty and resolves `icon` out of the
 * frozen Stripe table only. `defaultPane` fills both from the connector's
 * catalogue, which is the fix — but a caller on the old name encodes whatever
 * it is handed, and quietly turning a blank field into a stored override is
 * not something a deprecated alias should do to a merchant's saved config.
 */
export function defaultNativePane(method: string): Pane {
  const info = nativePaneMethodInfo(method);
  return {
    ...defaultPane(method),
    category: '',
    icon: info?.defaultIcon ?? '',
  };
}

/** @deprecated Renamed to {@link clonePane}. Removed in 0.112.0. */
export const cloneNativePane = clonePane;
/** @deprecated Renamed to {@link decodePanes}. Removed in 0.112.0. */
export const decodeNativePanes = decodePanes;
/** @deprecated Renamed to {@link encodePanes}. Removed in 0.112.0. */
export const encodeNativePanes = encodePanes;
